import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode
} from 'react';
import { Icon } from '../Icon';

type Kind = 'success' | 'danger' | 'info';

export interface ToastOptions {
  /** Rótulo de um botão de ação (ex.: "Desfazer"). */
  actionLabel?: string;
  /** Callback do botão de ação. Fecha o toast ao clicar. */
  onAction?: () => void;
  /** Duração em ms (default 3200; use maior para ações como Desfazer). */
  duration?: number;
}

interface Toast {
  id: string;
  msg: string;
  kind: Kind;
  actionLabel?: string;
  onAction?: () => void;
  leaving?: boolean;
}

type PushFn = (msg: string, kind?: Kind, opts?: ToastOptions) => void;

const ToastCtx = createContext<PushFn>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Marca o toast como saindo (anima) e remove após o fade-out (§1.5.6).
  const dismiss = useCallback((id: string) => {
    setToasts((t) => t.map((x) => (x.id === id ? { ...x, leaving: true } : x)));
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 180);
  }, []);

  const push = useCallback<PushFn>(
    (msg, kind = 'success', opts) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((t) => [...t, { id, msg, kind, actionLabel: opts?.actionLabel, onAction: opts?.onAction }]);
      const duration = opts?.duration ?? 3200;
      setTimeout(() => dismiss(id), duration);
    },
    [dismiss]
  );

  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div
        style={{
          position: 'fixed',
          bottom: 22,
          right: 22,
          zIndex: 500,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          alignItems: 'flex-end'
        }}
      >
        {toasts.map((t) => {
          const iconName = t.kind === 'success' ? 'check' : t.kind === 'danger' ? 'alert' : 'activity';
          const col = t.kind === 'success' ? 'var(--success)' : t.kind === 'danger' ? 'var(--danger)' : 'var(--info)';
          return (
            <div
              key={t.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: 'var(--bg-2)',
                border: '1px solid var(--border)',
                borderLeft: `3px solid ${col}`,
                borderRadius: 'var(--r-md)',
                padding: '11px 15px',
                boxShadow: 'var(--shadow-2)',
                fontSize: 13.5,
                color: 'var(--text-0)',
                minWidth: 240,
                maxWidth: 420,
                animation: t.leaving
                  ? 'toastOut 0.18s var(--ease) forwards'
                  : 'toastIn 0.26s var(--ease)'
              }}
            >
              <span style={{ color: col, display: 'flex' }}>
                <Icon name={iconName} size={16} />
              </span>
              <span style={{ flex: 1 }}>{t.msg}</span>
              {t.actionLabel && (
                <button
                  className="focusable"
                  onClick={() => {
                    t.onAction?.();
                    dismiss(t.id);
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-sm)',
                    color: col,
                    fontSize: 12.5,
                    fontWeight: 600,
                    padding: '4px 10px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {t.actionLabel}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </ToastCtx.Provider>
  );
}
